import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Folder as FolderIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchRoleplay,
  fetchRoleplayHistory,
  fetchRoleplayLinks,
  createRoleplayLink,
  updateRoleplayLink,
  updateRoleplayControlGraph,
  fetchRoleplayAssessedSessions,
} from "../../../redux/actions/roleplayActions";
import { fetchAllAgentConfigs } from "../../../redux/actions/agentConfigActions";
import RoleplayLinksTable from "./RoleplayLinksTable";
import RoleplayHistory from "./RoleplayHistory";
import RoleplayAssessedSessionsTable from "./RoleplayAssessedSessionsTable";
import { ControlGraphEditor } from "../assessments/control-graph-editor";

const RoleplayView: React.FC = () => {
  const { roleplayId, organizationId } = useParams<{
    roleplayId: string;
    organizationId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedResourceKey, setCopiedResourceKey] = useState<boolean>(false);
  const [controlGraphEditorOpen, setControlGraphEditorOpen] = useState(false);

  // Ref for the roleplay sessions section
  const roleplaySessionsRef = useRef<HTMLDivElement>(null);

  const {
    currentRoleplay,
    loading,
    error,
    roleplayHistory,
    historyLoading,
    linksLoading,
    roleplayAssessedSessions,
    sessionsLoading,
  } = useSelector((state: RootState) => state.roleplays);

  const { agentConfigs } = useSelector(
    (state: RootState) => state.agentConfigs
  );

  const allRoleplayLinks = useSelector(
    (state: RootState) => state.roleplays.roleplayLinks
  );
  const roleplayLinks = currentRoleplay?.id
    ? allRoleplayLinks[currentRoleplay.id] || []
    : [];

  useEffect(() => {
    if (roleplayId && organizationId) {
      dispatch(fetchRoleplay({ roleplayId, organizationId }));
      dispatch(fetchRoleplayLinks({ roleplayId, organizationId }));
      dispatch(fetchRoleplayAssessedSessions({ roleplayId, organizationId }));
    }
  }, [dispatch, roleplayId, organizationId]);

  useEffect(() => {
    if (currentRoleplay?.resource_key && currentRoleplay?.organization_id) {
      dispatch(
        fetchRoleplayHistory({
          resourceKey: currentRoleplay.resource_key,
          organizationId: currentRoleplay.organization_id,
        })
      );
    }
  }, [currentRoleplay, dispatch]);

  useEffect(() => {
    dispatch(fetchAllAgentConfigs());
  }, [dispatch]);

  // Handle scrolling to roleplay sessions section and filtering by chat session ID
  useEffect(() => {
    // Check if we have a hash to scroll to
    if (location.hash === '#roleplay-sessions' && roleplaySessionsRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        roleplaySessionsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [location.hash, roleplayAssessedSessions]);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyResourceKey = (resourceKey: string) => {
    navigator.clipboard.writeText(resourceKey);
    setCopiedResourceKey(true);
    setTimeout(() => setCopiedResourceKey(false), 2000);
  };

  const handleCreateLink = async (
    agentConfigId: string,
    displayName: string,
    testOverrideHumanDescription: string,
    additionalContext: string,
    controlGraphKey: string | null
  ) => {
    if (!roleplayId || !organizationId) {
      throw new Error("Missing roleplayId or organizationId");
    }

    await dispatch(
      createRoleplayLink({
        roleplayId,
        organizationId,
        agent_config_id: agentConfigId,
        display_name: displayName || null,
        test_override_human_description: testOverrideHumanDescription || undefined,
        additional_context: additionalContext || undefined,
        control_graph_key: controlGraphKey || undefined,
      })
    ).unwrap();
    toast.success("Link created successfully!");
    // Refresh links
    dispatch(fetchRoleplayLinks({ roleplayId, organizationId }));
  };

  const handleUpdateLink = async (updatedLink: any) => {
    await dispatch(
      updateRoleplayLink({
        linkId: updatedLink.id,
        ...updatedLink,
      })
    ).unwrap();
    toast.success("Link updated successfully!");
    // Refresh the links list
    if (roleplayId && organizationId) {
      dispatch(
        fetchRoleplayLinks({
          roleplayId,
          organizationId,
        })
      );
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <IconButton onClick={() => navigate("/dashboard/roleplays")}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
    );
  }

  if (!currentRoleplay) {
    return (
      <Box p={3}>
        <Typography>Roleplay not found</Typography>
        <IconButton onClick={() => navigate("/dashboard/roleplays")}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate("/dashboard/roleplays")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Roleplay Details</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AccountTreeIcon />}
          onClick={() => setControlGraphEditorOpen(true)}
          color="secondary"
        >
          Edit Control Graph
        </Button>
      </Box>

      {/* Metadata Card */}
      <Grid item xs={12}>
        <Card elevation={1}>
          <CardContent>
            {currentRoleplay.display_title && (
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {currentRoleplay.display_title}
                </Typography>
              </Box>
            )}
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Organization:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {currentRoleplay.organization_id}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Chip
                label={currentRoleplay.environment}
                size="small"
                variant="outlined"
                color={
                  currentRoleplay.environment === "production"
                    ? "error"
                    : currentRoleplay.environment === "staging"
                    ? "warning"
                    : "default"
                }
              />
              <Box sx={{ flexGrow: 1 }} />
              <Box display="flex" alignItems="center" gap={1}>
                {currentRoleplay.is_active ? (
                  <>
                    <CheckCircleIcon fontSize="small" color="success" />
                    <Typography
                      variant="body2"
                      color="success.main"
                      fontWeight="medium"
                    >
                      Active
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Inactive
                  </Typography>
                )}
              </Box>
            </Box>
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                ID: {currentRoleplay.id}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyId(currentRoleplay.id)}
                sx={{ p: 0.25 }}
              >
                {copiedId === currentRoleplay.id ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Box>
            {currentRoleplay.resource_key && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  Resource Key: {currentRoleplay.resource_key}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    handleCopyResourceKey(currentRoleplay.resource_key!)
                  }
                  sx={{ p: 0.25 }}
                >
                  {copiedResourceKey ? (
                    <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              Created:{" "}
              {new Date(currentRoleplay.creation_time).toLocaleString()} |
              Modified:{" "}
              {new Date(currentRoleplay.modified_time).toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid container spacing={3}>
        {/* Links Section - Moved to top */}
        <Grid item xs={12}>
          <RoleplayLinksTable
            roleplayLinks={roleplayLinks}
            linksLoading={linksLoading}
            agentConfigs={agentConfigs}
            organizationId={organizationId || ""}
            roleplayId={roleplayId || ""}
            onCreateLink={handleCreateLink}
            onUpdateLink={handleUpdateLink}
          />
        </Grid>

        {/* Scenario Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scenario Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.description || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Human Role
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.human_role || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Bot Role
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.bot_role || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Greeting
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.greeting || "-"}
                </Typography>
              </Grid>

              {currentRoleplay.criteria_context && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Criteria Context
                  </Typography>
                  <Typography variant="body1">
                    {currentRoleplay.criteria_context}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Concerns */}
        {currentRoleplay.generated_concerns &&
          currentRoleplay.generated_concerns.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Concerns ({currentRoleplay.generated_concerns.length})
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {currentRoleplay.generated_concerns.map((concern, index) => (
                  <Accordion key={concern.id} defaultExpanded={index === 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        width="100%"
                      >
                        <Typography variant="subtitle1" fontWeight="medium">
                          {concern.question}
                        </Typography>
                        {concern.bonus && (
                          <Chip label="Bonus" size="small" color="secondary" />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          ID: {concern.id}
                        </Typography>

                        {concern.context && (
                          <Box mt={2}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                            >
                              Context
                            </Typography>
                            <Typography variant="body2">
                              {concern.context}
                            </Typography>
                          </Box>
                        )}

                        {concern.rubric && concern.rubric.length > 0 && (
                          <Box mt={2}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Rubric
                            </Typography>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Score</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Example</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {concern.rubric.map((rubricItem) => (
                                    <TableRow key={rubricItem.score}>
                                      <TableCell>
                                        <Chip
                                          label={rubricItem.score}
                                          size="small"
                                          color={
                                            rubricItem.score >= 4
                                              ? "success"
                                              : rubricItem.score >= 3
                                              ? "primary"
                                              : "default"
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {rubricItem.description}
                                      </TableCell>
                                      <TableCell>
                                        {rubricItem.example || "-"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            </Grid>
          )}

        {/* Scene Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scene Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scene Type
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.scene_type}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Show Wrap
                </Typography>
                <Chip
                  label={currentRoleplay.show_wrap ? "Yes" : "No"}
                  size="small"
                  color={currentRoleplay.show_wrap ? "success" : "default"}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Show Emotion Cards
                </Typography>
                <Chip
                  label={currentRoleplay.show_emotion_cards ? "Yes" : "No"}
                  size="small"
                  color={
                    currentRoleplay.show_emotion_cards ? "success" : "default"
                  }
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Can Swear When Angry
                </Typography>
                <Chip
                  label={currentRoleplay.can_swear_angry ? "Yes" : "No"}
                  size="small"
                  color={
                    currentRoleplay.can_swear_angry ? "warning" : "default"
                  }
                />
              </Grid>

              {currentRoleplay.expected_duration_minutes && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Expected Duration
                  </Typography>
                  <Typography variant="body1">
                    {currentRoleplay.expected_duration_minutes} minutes
                  </Typography>
                </Grid>
              )}

              {currentRoleplay.end_upset_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    End Upset Message
                  </Typography>
                  <Typography variant="body2">
                    {currentRoleplay.end_upset_message}
                  </Typography>
                </Grid>
              )}

              {currentRoleplay.end_disengaged_message && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    End Disengaged Message
                  </Typography>
                  <Typography variant="body2">
                    {currentRoleplay.end_disengaged_message}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Emotional State */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <EmojiEmotionsIcon color="primary" />
              <Typography variant="h6">Emotional State</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {currentRoleplay.emotion_guidance && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Emotion Guidance
                  </Typography>
                  <Typography variant="body2">
                    {currentRoleplay.emotion_guidance}
                  </Typography>
                </Grid>
              )}

              {currentRoleplay.valence_guidance && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Valence Guidance
                  </Typography>
                  <Typography variant="body2">
                    {currentRoleplay.valence_guidance}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Starting Engagement
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.starting_engagement.toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Starting Valence
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.starting_valence.toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Starting Activation
                </Typography>
                <Typography variant="body1">
                  {currentRoleplay.starting_activation.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Assets */}
        {(currentRoleplay.response_namespace_assets ||
          currentRoleplay.faq_namespace_assets) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FolderIcon color="primary" />
                <Typography variant="h6">Assets</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {currentRoleplay.response_namespace_assets && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Response Assets
                    </Typography>
                    <Box mt={1}>
                      {Object.entries(
                        currentRoleplay.response_namespace_assets
                          .namespace_assets || {}
                      ).map(([namespace, assetIds]) => (
                        <Box key={namespace} mb={1}>
                          <Typography variant="caption" color="text.secondary">
                            {namespace}:
                          </Typography>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                            mt={0.5}
                          >
                            {(assetIds as string[]).map((assetId: string) => (
                              <Chip
                                key={assetId}
                                label={assetId}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={() =>
                                  navigate(
                                    `/dashboard/assets/${namespace}/${assetId}`
                                  )
                                }
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    borderColor: "primary.main",
                                    color: "primary.contrastText",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}

                {currentRoleplay.faq_namespace_assets && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      FAQ Assets
                    </Typography>
                    <Box mt={1}>
                      {Object.entries(
                        currentRoleplay.faq_namespace_assets.namespace_assets ||
                          {}
                      ).map(([namespace, assetIds]) => (
                        <Box key={namespace} mb={1}>
                          <Typography variant="caption" color="text.secondary">
                            {namespace}:
                          </Typography>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                            mt={0.5}
                          >
                            {(assetIds as string[]).map((assetId: string) => (
                              <Chip
                                key={assetId}
                                label={assetId}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={() =>
                                  navigate(
                                    `/dashboard/assets/${namespace}/${assetId}`
                                  )
                                }
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    borderColor: "primary.main",
                                    color: "primary.contrastText",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Order/Steps */}
        {currentRoleplay.order && currentRoleplay.order.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Scenario Steps
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {currentRoleplay.order.map((step, index) => (
                  <Chip
                    key={index}
                    label={`${index + 1}. ${step}`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Roleplay Sessions */}
        <Grid item xs={12} ref={roleplaySessionsRef} id="roleplay-sessions">
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Roleplay Sessions
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <RoleplayAssessedSessionsTable
              assessedSessions={roleplayAssessedSessions}
              sessionsLoading={sessionsLoading}
              roleplayId={roleplayId || ""}
              organizationId={organizationId || ""}
              initialChatSessionFilter={new URLSearchParams(location.search).get('chat_session_id') || undefined}
            />
          </Paper>
        </Grid>

        {/* Version History */}
        {currentRoleplay.resource_key && roleplayHistory.length > 0 && (
          <Grid item xs={12}>
            <RoleplayHistory
              currentRoleplayId={currentRoleplay.id}
              roleplayHistory={roleplayHistory}
              historyLoading={historyLoading}
            />
          </Grid>
        )}
      </Grid>

      {/* Control Graph Editor Dialog */}
      <Dialog
        open={controlGraphEditorOpen}
        onClose={() => setControlGraphEditorOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTreeIcon />
              Control Graph Editor
            </Box>
            <Button onClick={() => setControlGraphEditorOpen(false)} color="inherit">
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "85vh" }}>
          <ControlGraphEditor
            controlGraph={currentRoleplay.control_graph || null}
            onSave={async (controlGraph) => {
              try {
                if (!currentRoleplay) {
                  toast.error("Current roleplay not found");
                  return;
                }

                // Update ONLY the control graph - backend handles keeping other fields unchanged
                const result = await dispatch(
                  updateRoleplayControlGraph({
                    roleplayId: roleplayId!,
                    organizationId: currentRoleplay.organization_id,
                    control_graph: controlGraph,
                  })
                ).unwrap();

                toast.success("Control graph saved! Redirecting to new version...");
                setControlGraphEditorOpen(false);

                // Redirect to new version if created
                if (result.id && result.id !== roleplayId) {
                  setTimeout(() => {
                    navigate(
                      `/dashboard/roleplays/${result.organization_id}/${result.id}`,
                      { replace: true }
                    );
                  }, 500);
                } else {
                  // Refresh current roleplay
                  dispatch(
                    fetchRoleplay({
                      roleplayId: roleplayId!,
                      organizationId: currentRoleplay.organization_id,
                    })
                  );
                }
              } catch {
                toast.error("Failed to save control graph");
              }
            }}
            onClose={() => setControlGraphEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Box>
  );
};

export default RoleplayView;
